import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  TextField,
  Typography,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import UploadIcon from '@mui/icons-material/Upload';
import {
  ImportPreviewResponse,
  adminGetImportTemplate,
  adminImportSet,
  adminPreviewImport,
  getErrorMessage,
} from '../provApi';

const BRAND_COLOR = '#1a365d';

interface ProvImportPanelProps {
  onImported: () => void;
  onCancel: () => void;
}

export default function ProvImportPanel({ onImported, onCancel }: ProvImportPanelProps) {
  const [importTitle, setImportTitle] = useState('');
  const [importSubject, setImportSubject] = useState('');
  const [importSource, setImportSource] = useState('');
  const [importText, setImportText] = useState('');
  const [preview, setPreview] = useState<ImportPreviewResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const payload = {
    title: importTitle.trim() || undefined,
    subject: importSubject.trim() || undefined,
    source: importSource.trim() || undefined,
    rawText: importText,
  };

  const handleDownloadTemplate = async () => {
    try {
      const template = await adminGetImportTemplate();
      const blob = new Blob([JSON.stringify(template, null, 2)], { type: 'application/json;charset=utf-8' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'provset-mall-v1.json';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(getErrorMessage(err));
    }
  };

  const handleCopyPrompt = async () => {
    try {
      const template = await adminGetImportTemplate();
      await navigator.clipboard.writeText(template.gptPrompt || template.instructions);
      setInfo('GPT-instruktionen är kopierad.');
    } catch (err) {
      setError(getErrorMessage(err));
    }
  };

  const handleFile = async (file: File) => {
    const text = await file.text();
    setImportText(text);
    setPreview(null);
  };

  const handlePreview = async () => {
    if (!importText.trim()) {
      setError('Text eller JSON-fil krävs.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const result = await adminPreviewImport(payload);
      setPreview(result);
      if (!result.ok) {
        setError(result.errors[0]?.message || 'Förhandsgranskningen hittade fel.');
      }
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleImport = async () => {
    if (!preview?.ok) {
      setError('Förhandsgranska och rätta felen innan du importerar.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const result = await adminImportSet(payload);
      setInfo(`Importerade ${result.importedCount} frågor.`);
      onImported();
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Typography sx={{ fontWeight: 700, mb: { xs: 2, sm: 3 }, fontSize: { xs: '1.3rem', sm: '1.5rem' } }}>
        Importera provset
      </Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {info && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setInfo(null)}>{info}</Alert>}

      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
        <Button variant="outlined" startIcon={<DownloadIcon />} onClick={handleDownloadTemplate} sx={{ minHeight: 44, textTransform: 'none' }}>
          Ladda ned mall
        </Button>
        <Button variant="outlined" startIcon={<ContentCopyIcon />} onClick={handleCopyPrompt} sx={{ minHeight: 44, textTransform: 'none' }}>
          Kopiera GPT-instruktion
        </Button>
        <Button variant="outlined" component="label" startIcon={<UploadIcon />} sx={{ minHeight: 44, textTransform: 'none' }}>
          Välj JSON-fil
          <input
            hidden
            type="file"
            accept="application/json,.json,.txt"
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (file) handleFile(file);
              event.target.value = '';
            }}
          />
        </Button>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
        <TextField label="Titel (valfritt om JSON redan har titel)" value={importTitle} onChange={(e) => setImportTitle(e.target.value)} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, minHeight: 52 } }} />
        <TextField label="Ämne" value={importSubject} onChange={(e) => setImportSubject(e.target.value)} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, minHeight: 52 } }} />
        <TextField label="Källa" value={importSource} onChange={(e) => setImportSource(e.target.value)} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, minHeight: 52 } }} />
        <TextField
          label="Klistra in JSON eller numrerad text"
          value={importText}
          onChange={(e) => {
            setImportText(e.target.value);
            setPreview(null);
          }}
          onDragOver={(event) => event.preventDefault()}
          onDrop={async (event) => {
            event.preventDefault();
            const file = event.dataTransfer.files?.[0];
            if (file) {
              await handleFile(file);
            }
          }}
          multiline
          minRows={8}
          helperText="Du kan också släppa en JSON-fil här."
        />
      </Box>

      {preview && (
        <Card sx={{ mb: 2, borderRadius: 2 }}>
          <CardContent>
            <Typography sx={{ fontWeight: 700, mb: 1 }}>
              Förhandsgranskning: {preview.parsed?.title || 'utan titel'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {preview.parsed?.questionCount || 0} frågor · {preview.parsed?.type || 'standard'}
            </Typography>
            {(preview.parsed?.sampleQuestions || []).map((question, index) => (
              <Typography key={index} variant="body2" sx={{ mb: 0.5 }}>
                {index + 1}. {question.prompt} — {question.answer}
              </Typography>
            ))}
            {preview.warnings.map((warning, index) => (
              <Alert key={`w-${index}`} severity="warning" sx={{ mt: 1 }}>{warning.message}</Alert>
            ))}
          </CardContent>
        </Card>
      )}

      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        <Button variant="outlined" onClick={onCancel} sx={{ minHeight: 48 }}>Avbryt</Button>
        <Button variant="outlined" onClick={handlePreview} disabled={loading} sx={{ minHeight: 48, textTransform: 'none' }}>
          {loading ? <CircularProgress size={20} /> : 'Förhandsgranska'}
        </Button>
        <Button
          variant="contained"
          onClick={handleImport}
          disabled={loading || !preview?.ok}
          sx={{ minHeight: 48, bgcolor: BRAND_COLOR, textTransform: 'none' }}
        >
          Importera
        </Button>
      </Box>
    </Box>
  );
}
